import React, { useEffect, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { ownerNav } from '@/lib/navConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Building2, Landmark, Save, Percent, Coins, Plus, Trash2, Truck, Star, History } from 'lucide-react';
import api, { fmtIDR, fmtDate } from '@/lib/api';
import { toast } from 'sonner';

export default function OwnerSettings() {
  const [s, setS] = useState({
    company_name: '', company_address: '', company_phone: '', company_email: '', tax_number: '',
    bank_name: '', bank_account_no: '', bank_account_holder: '', invoice_footer_note: '',
  });
  const [saving, setSaving] = useState(false);

  // Konfigurasi komisi sales (dipisah supaya patch endpoint-nya terpisah)
  const [commission, setCommission] = useState({
    non_iron_rate: 0.02,          // 2% default
    iron_per_kg_default: 50,      // Rp 50/kg default
    iron_per_kg_overrides: {},    // { "Besi Beton": 60, "IWF": 100, ... }
  });
  const [ironTypes, setIronTypes] = useState([]); // list jenis besi dari /iron/prices (untuk populate override)
  const [savingComm, setSavingComm] = useState(false);
  const [newOverrideKey, setNewOverrideKey] = useState('');

  useEffect(() => {
    api.get('/settings/company')
      .then(({ data }) => setS((prev) => ({ ...prev, ...data })))
      .catch((e) => console.error('[OwnerSettings] gagal load company settings:', e));
    api.get('/settings/commission')
      .then(({ data }) => setCommission((prev) => ({ ...prev, ...data })))
      .catch((e) => console.error('[OwnerSettings] gagal load commission:', e));
    // Load jenis besi supaya user bisa pilih dari daftar existing
    api.get('/iron/prices').then(({ data }) => {
      // Endpoint return { types: [...] }. Fallback ke array kalau format lain.
      const arr = Array.isArray(data) ? data : (data?.types || data?.entries || []);
      setIronTypes(arr);
    }).catch((e) => {
      console.warn('[OwnerSettings] gagal load iron/prices:', e);
      setIronTypes([]);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/settings/company', s);
      toast.success('Pengaturan tersimpan');
      setS((prev) => ({ ...prev, ...data }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
    finally { setSaving(false); }
  };

  const saveCommission = async () => {
    // Sanitize overrides: hilangkan key kosong / value non-angka
    const cleaned = {};
    Object.entries(commission.iron_per_kg_overrides || {}).forEach(([k, v]) => {
      const key = String(k || '').trim();
      const val = Number(v);
      if (key && !isNaN(val) && val >= 0) cleaned[key] = val;
    });
    setSavingComm(true);
    try {
      const { data } = await api.patch('/settings/commission', {
        non_iron_rate: Number(commission.non_iron_rate),
        iron_per_kg_default: Number(commission.iron_per_kg_default),
        iron_per_kg_overrides: cleaned,
      });
      toast.success('Konfigurasi komisi tersimpan (berlaku untuk transaksi BARU)');
      setCommission((prev) => ({ ...prev, ...data }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan komisi'); }
    finally { setSavingComm(false); }
  };

  const update = (k) => (e) => setS({ ...s, [k]: e.target.value });

  // Helpers untuk edit override map
  const setOverride = (key, value) => {
    setCommission((prev) => ({
      ...prev,
      iron_per_kg_overrides: { ...prev.iron_per_kg_overrides, [key]: value },
    }));
  };
  const removeOverride = (key) => {
    setCommission((prev) => {
      const next = { ...prev.iron_per_kg_overrides };
      delete next[key];
      return { ...prev, iron_per_kg_overrides: next };
    });
  };
  const addOverride = () => {
    const k = (newOverrideKey || '').trim();
    if (!k) { toast.error('Pilih atau ketik nama jenis besi'); return; }
    if (commission.iron_per_kg_overrides?.[k] != null) { toast.error(`Override untuk "${k}" sudah ada`); return; }
    setOverride(k, commission.iron_per_kg_default);
    setNewOverrideKey('');
  };

  const nonIronPct = (Number(commission.non_iron_rate) || 0) * 100;
  const overrideEntries = Object.entries(commission.iron_per_kg_overrides || {});
  // Jenis besi yang belum ada override — untuk suggestions di add form
  const availableTypes = (ironTypes || []).filter((t) => !(t.iron_type in (commission.iron_per_kg_overrides || {})));

  return (
    <AppShell nav={ownerNav} title="Pengaturan Perusahaan" showBell>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Building2 className="h-4 w-4" />Identitas Perusahaan</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5"><Label>Nama Perusahaan</Label><Input value={s.company_name || ''} onChange={update('company_name')} data-testid="settings-company-name" /></div>
            <div className="space-y-1.5"><Label>Alamat</Label><Textarea value={s.company_address || ''} onChange={update('company_address')} rows={2} data-testid="settings-company-address" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Telepon</Label><Input value={s.company_phone || ''} onChange={update('company_phone')} data-testid="settings-company-phone" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input value={s.company_email || ''} onChange={update('company_email')} data-testid="settings-company-email" /></div>
            </div>
            <div className="space-y-1.5"><Label>NPWP Perusahaan</Label><Input value={s.tax_number || ''} onChange={update('tax_number')} data-testid="settings-tax" /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><Landmark className="h-4 w-4" />Rekening Pembayaran (Muncul di Invoice)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5"><Label>Nama Bank</Label><Input value={s.bank_name || ''} onChange={update('bank_name')} placeholder="mis. BCA / Mandiri / BRI" data-testid="settings-bank-name" /></div>
            <div className="space-y-1.5"><Label>Nomor Rekening</Label><Input value={s.bank_account_no || ''} onChange={update('bank_account_no')} placeholder="1234567890" data-testid="settings-bank-no" /></div>
            <div className="space-y-1.5"><Label>Atas Nama</Label><Input value={s.bank_account_holder || ''} onChange={update('bank_account_holder')} data-testid="settings-bank-holder" /></div>
            <div className="space-y-1.5"><Label>Catatan Kaki Invoice</Label><Textarea value={s.invoice_footer_note || ''} onChange={update('invoice_footer_note')} rows={2} placeholder="mis. Terima kasih atas kerjasamanya." data-testid="settings-footer-note" /></div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-4 max-w-5xl">
        <Button onClick={save} disabled={saving} data-testid="settings-save-button"><Save className="h-4 w-4 mr-2" />{saving ? 'Menyimpan...' : 'Simpan Pengaturan'}</Button>
      </div>

      {/* Card Konfigurasi Komisi Sales (bagi hasil) */}
      <div className="mt-8 max-w-5xl">
        <Card data-testid="commission-config-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Coins className="h-4 w-4" /> Konfigurasi Komisi Sales (Bagi Hasil)
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Perubahan bersifat <b>prospective</b> — hanya berlaku untuk transaksi <b>baru</b>. Transaksi lama tetap dengan rate saat itu.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Row 1: Non-besi rate */}
            <div className="rounded-md border p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Percent className="h-3.5 w-3.5 text-primary" /> Barang Non-Besi/Baja
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Rate Komisi (%)</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number" min="0" max="100" step="0.1"
                      value={nonIronPct}
                      onChange={(e) => setCommission((prev) => ({ ...prev, non_iron_rate: (Number(e.target.value) || 0) / 100 }))}
                      className="tabular-nums"
                      data-testid="commission-non-iron-rate"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground pb-2">
                  Contoh: subtotal Rp 1.000.000 × {nonIronPct}% = <b className="text-primary tabular-nums">{fmtIDR(1000000 * (nonIronPct / 100))}</b>
                </div>
              </div>
            </div>

            {/* Row 2: Iron default */}
            <div className="rounded-md border p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Coins className="h-3.5 w-3.5 text-amber-600" /> Barang Besi/Baja
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Rate Default per Kg (Rp)</Label>
                  <Input
                    type="number" min="0" step="1"
                    value={commission.iron_per_kg_default}
                    onChange={(e) => setCommission((prev) => ({ ...prev, iron_per_kg_default: Number(e.target.value) || 0 }))}
                    className="tabular-nums"
                    data-testid="commission-iron-default"
                  />
                </div>
                <div className="text-xs text-muted-foreground pb-2">
                  Contoh: 100 kg × Rp {Number(commission.iron_per_kg_default || 0).toLocaleString('id-ID')} = <b className="text-amber-700 tabular-nums">{fmtIDR(100 * Number(commission.iron_per_kg_default || 0))}</b>
                </div>
              </div>

              {/* Override per jenis besi */}
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs font-semibold">Override per Jenis Besi (opsional)</Label>
                  <span className="text-[10px] text-muted-foreground">Jenis yang tidak di-list pakai rate default di atas.</span>
                </div>
                {overrideEntries.length === 0 ? (
                  <div className="text-xs italic text-muted-foreground py-1">Belum ada override — semua jenis besi pakai Rp {Number(commission.iron_per_kg_default || 0).toLocaleString('id-ID')}/kg.</div>
                ) : (
                  <div className="space-y-1.5">
                    {overrideEntries.map(([key, val]) => (
                      <div key={key} className="flex items-center gap-2" data-testid={`commission-override-${key}`}>
                        <div className="flex-1 text-xs font-medium truncate">{key}</div>
                        <div className="text-xs text-muted-foreground">Rp</div>
                        <Input
                          type="number" min="0" step="1"
                          value={val}
                          onChange={(e) => setOverride(key, Number(e.target.value) || 0)}
                          className="h-7 w-28 text-xs tabular-nums text-right"
                          data-testid={`commission-override-input-${key}`}
                        />
                        <span className="text-xs text-muted-foreground">/kg</span>
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"
                          onClick={() => removeOverride(key)}
                          data-testid={`commission-override-remove-${key}`}
                          title={`Hapus override "${key}"`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Add override */}
                <div className="mt-2 flex items-center gap-2">
                  <select
                    value={newOverrideKey}
                    onChange={(e) => setNewOverrideKey(e.target.value)}
                    className="h-8 flex-1 px-2 text-xs rounded-md border bg-background"
                    data-testid="commission-override-add-select"
                  >
                    <option value="">Pilih jenis besi (atau ketik manual)...</option>
                    {availableTypes.map((t) => (
                      <option key={t.iron_type} value={t.iron_type}>{t.iron_type}</option>
                    ))}
                  </select>
                  <Input
                    placeholder="atau ketik nama"
                    value={newOverrideKey}
                    onChange={(e) => setNewOverrideKey(e.target.value)}
                    className="h-8 w-40 text-xs"
                    data-testid="commission-override-add-input"
                  />
                  <Button size="sm" variant="outline" onClick={addOverride} data-testid="commission-override-add-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Tambah
                  </Button>
                </div>
              </div>
            </div>

            <Button onClick={saveCommission} disabled={savingComm} data-testid="commission-save-btn">
              <Save className="h-4 w-4 mr-2" />
              {savingComm ? 'Menyimpan...' : 'Simpan Konfigurasi Komisi'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Master Sopir & Kendaraan */}
      <div className="mt-8 max-w-5xl">
        <DriverMasterCard />
      </div>
    </AppShell>
  );
}


function DriverMasterCard() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newDriver, setNewDriver] = useState('');
  const [newPlate, setNewPlate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings/drivers');
      setDrivers(data.drivers || []);
    } catch (_e) { setDrivers([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addDriver = async () => {
    const dn = newDriver.trim();
    const pl = newPlate.trim().toUpperCase();
    if (!dn || !pl) return toast.error('Nama sopir dan plat wajib');
    setSaving(true);
    try {
      await api.post('/settings/drivers', { driver_name: dn, vehicle_plate: pl });
      toast.success(`Sopir "${dn}" ditambahkan ke master`);
      setNewDriver(''); setNewPlate('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal tambah'); }
    finally { setSaving(false); }
  };

  const removeDriver = async (id, name) => {
    try {
      await api.delete(`/settings/drivers/${id}`);
      toast.success(`"${name}" dihapus dari master`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal hapus'); }
  };

  const master = drivers.filter((d) => d.source === 'master');
  const history = drivers.filter((d) => d.source === 'history');

  return (
    <Card data-testid="driver-master-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Truck className="h-4 w-4" /> Master Sopir & Kendaraan
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Sopir yang ditandai <Star className="h-3 w-3 inline fill-amber-400 text-amber-500" /> akan muncul di paling atas
          autocomplete saat kasir/admin isi info pengiriman. Histori (<History className="h-3 w-3 inline" />) diambil otomatis
          dari transaksi lama.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add form */}
        <div className="rounded-md border p-3 bg-accent/20 space-y-2">
          <Label className="text-xs font-semibold">Tambah Sopir ke Master</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input
              placeholder="Nama Sopir (mis. Pak Slamet)"
              value={newDriver}
              onChange={(e) => setNewDriver(e.target.value)}
              className="text-sm"
              data-testid="driver-master-add-name"
            />
            <Input
              placeholder="Plat (B 1234 XYZ)"
              value={newPlate}
              onChange={(e) => setNewPlate(e.target.value.toUpperCase())}
              className="text-sm font-mono"
              data-testid="driver-master-add-plate"
            />
            <Button onClick={addDriver} disabled={saving} data-testid="driver-master-add-btn">
              <Plus className="h-4 w-4 mr-1" /> {saving ? 'Menyimpan...' : 'Tambah'}
            </Button>
          </div>
        </div>

        {/* Master list */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />
            <span className="text-sm font-semibold">Master (dikelola Owner)</span>
            <Badge variant="outline" className="text-[10px]">{master.length}</Badge>
          </div>
          {loading ? (
            <div className="text-xs text-muted-foreground py-2">Memuat...</div>
          ) : master.length === 0 ? (
            <div className="text-xs italic text-muted-foreground py-2 px-3 border rounded-md">
              Belum ada. Tambahkan sopir tetap yang sering dipakai di form atas.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Nama Sopir</TableHead>
                  <TableHead className="text-xs">Plat</TableHead>
                  <TableHead className="text-xs text-right">Total Kirim</TableHead>
                  <TableHead className="text-xs w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {master.map((d) => (
                  <TableRow key={d.id} data-testid={`driver-master-row-${d.id}`}>
                    <TableCell className="text-sm font-medium">{d.driver_name}</TableCell>
                    <TableCell className="text-xs font-mono">{d.vehicle_plate}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums text-muted-foreground">
                      {d.count > 0 ? `${d.count}x` : '-'}
                    </TableCell>
                    <TableCell>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"
                            data-testid={`driver-master-remove-${d.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Hapus dari master?</AlertDialogTitle>
                            <AlertDialogDescription>
                              <b>{d.driver_name}</b> ({d.vehicle_plate}) akan hilang dari daftar master.
                              Riwayat transaksi lama TIDAK terpengaruh — data batch tetap ada.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Batal</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => removeDriver(d.id, d.driver_name)}
                              className="bg-rose-600 hover:bg-rose-700"
                              data-testid={`driver-master-remove-confirm-${d.id}`}
                            >Ya, Hapus</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* History summary — read-only */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <History className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-semibold">Auto dari Histori Pengiriman</span>
            <Badge variant="outline" className="text-[10px]">{history.length}</Badge>
            <span className="text-[10px] text-muted-foreground">(read-only — dari transaksi lama)</span>
          </div>
          {history.length === 0 ? (
            <div className="text-xs italic text-muted-foreground py-2 px-3 border rounded-md">
              Belum ada histori pengiriman. Setelah ada transaksi dengan sopir, list otomatis terisi di sini.
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto rounded-md border">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead className="text-xs">Sopir</TableHead>
                    <TableHead className="text-xs">Plat</TableHead>
                    <TableHead className="text-xs text-right">Jumlah</TableHead>
                    <TableHead className="text-xs">Terakhir</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.slice(0, 20).map((d, idx) => (
                    <TableRow key={`hist-${idx}`}>
                      <TableCell className="text-xs">{d.driver_name}</TableCell>
                      <TableCell className="text-xs font-mono">{d.vehicle_plate}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{d.count}x</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {d.last_used ? fmtDate(d.last_used) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {history.length > 20 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-[10px] italic text-muted-foreground">
                        …dan {history.length - 20} lagi (tampilkan 20 terbaru)
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
